// @flow
import React, { useState, useEffect } from 'react';
import type { Element } from 'react';
import { connect } from 'react-redux';
import { NextSeo } from 'next-seo';
import Grid from 'styled-components-grid';
import compose from 'lodash/fp/compose';
import { omit } from 'lodash';
import { FINDER_TREATMENT, YAPITA_HEALTH_URL, CUREFY_URL } from '@routes';

import { withTranslation, Router, nextRoutes } from '@i18n';

import type { GlobalState } from '@globalTypes';

import { sortQueryParams } from '@lib/helpers';
import SearchBar from './SearchBar';
import SearchResultList from './SearchResultList';
import type { Props } from './types';
import { departmentList } from '../department';
import { requestTreatmentList, resetTreatmentList } from './redux/actions';

import { StyledPagination, StyledResultListContainer } from './styles';

const TreatmentFinder = ({
  objectList,
  requestingobjectList,
  requestTreatmentList,
  numberOfPages,
  resetTreatmentList,
  query,
  curefyDomain,
  t
}: Props): Element<any> => {
  const [showMap, setPlaying] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const { locale } = nextRoutes;
  useEffect((): (() => void) => resetTreatmentList, []);

  useEffect((): void => {
    if (isFirstLoad) setIsFirstLoad(false);
    const removalKeys = [];
    if (query.department === 'all-treatment') {
      removalKeys.push('department');
    }
    if (!isFirstLoad) requestTreatmentList(omit(query, removalKeys));
  }, [query]);
  const { department, pageNumber, query: searchQuery } = query;
  const setPage = (pageNumber: number): void => {
    const params = sortQueryParams({ ...omit(query, 'nextRoute'), pageNumber }, [
      'query',
      'department',
      'pageNumber'
    ]);
    Router.pushRoute(FINDER_TREATMENT, params);
  };

  const queryPageNumber = parseInt(query?.pageNumber, 10) || 1;

  const metaData: {
    MetaTitle: string,
    MetaDescription: string,
  } = t('Seo:TreatmentListing', {
    returnObjects: true,
    department: department && department !== 'all-treatment' ? (departmentList[department]) : '',
    pageNumber: pageNumber ? ('(Page ' + pageNumber + ')') : ''
  });

  const generateUrl = () => {
    let baseUrl = `${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/treatments`;
    if (searchQuery) {
      baseUrl += `?query=${searchQuery.replace(/ /g, '%20')}`;
    }
    if (pageNumber) {
      if (baseUrl.includes('?')) {
        baseUrl += `&pageNumber=${pageNumber}`;
      } else {
        baseUrl += `?pageNumber=${pageNumber}`;
      }
    }
    return baseUrl;
  };

  return (
    <>
      <NextSeo
        title={`${metaData.MetaTitle} | ${curefyDomain ? 'Curefy' : 'Yapita Health'}`}
        description={`${metaData.MetaDescription} | ${curefyDomain ? 'Curefy' : 'Yapita Health'}`}
        canonical={generateUrl()}
        openGraph={{
          url: generateUrl(),
          title: `${metaData.MetaTitle} - ${curefyDomain ? 'Curefy' : 'Yapita Health'}`,
          description: `${metaData.MetaDescription} - ${curefyDomain ? 'Curefy' : 'Yapita Health'}`,
          images: [
            {
              url:
                'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: curefyDomain ? 'Curefy' : 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />

      <React.Fragment>
        <SearchBar
          toggleMap={(): void => {
            window.scrollTo(0, 0);
            setPlaying(!showMap);
          }}
          isMapVisible={showMap}
          query={omit(query, 'nextRoute')}
          numberOfPages={numberOfPages}
          objectList={objectList}
          city={query.location}
          department={query.department}
          position={query.position}
        />

        <Grid>
          <StyledResultListContainer
            visible={{ mobile: !showMap, desktop: true }}
            size={{ mobile: 12 / 12, desktop: !showMap ? 12 / 12 : 7 / 12 }}
          >
            <SearchResultList
              isMapVisible={showMap}
              loading={requestingobjectList}
              objectList={objectList}
              city={query.location}
              department={query.department}
              position={query.position}
              numberOfPages={numberOfPages}
            />
          </StyledResultListContainer>
        </Grid>

        <StyledPagination
          initialPage={queryPageNumber - 1}
          numberOfPages={numberOfPages}
          setPage={setPage}
          pathname="/finder/treatment"
        />
      </React.Fragment>
    </>
  );
};

TreatmentFinder.getInitialProps = async ({ query, store }) => {
  const removalKeys = [];
  if (query.department === 'all-treatment') {
    removalKeys.push('department');
  }
  store.dispatch(requestTreatmentList(omit(query, removalKeys)));
  const { getState } = store;
  const { page } = getState();
  const { curefyDomain } = page;

  return {
    namespacesRequired: ['Treatments'],
    query,
    curefyDomain
  };
};

const mapStateToProps = (state: GlobalState): {} => ({
  numberOfPages: state.treatmentFinder.numberOfPages,
  objectList: state.treatmentFinder.data,
  requestingobjectList: state.treatmentFinder.getStatus?.requesting
});

export default compose(
  connect(
    mapStateToProps,
    {
      requestTreatmentList,
      resetTreatmentList
    }
  ),
  withTranslation(['Treatments'])
)(TreatmentFinder);
